import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Books, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { InlineRichHtmlEditor, htmlToPlain, sanitizeHtml } from "@/components/RichHtmlEditorDialog";

export default function KnowledgeBases() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null | {id?, name, content}
  const [contentOverLimit, setContentOverLimit] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/knowledge-bases");
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    const name = (editor?.name || "").trim();
    if (!name) { toast.error("Name is required"); return; }
    if (contentOverLimit) {
      toast.error("Content is over the character limit");
      return;
    }
    const content = sanitizeHtml(editor?.content || "");
    try {
      if (editor.id) {
        await api.patch(`/knowledge-bases/${editor.id}`, { name, content });
        toast.success("Knowledge base updated");
      } else {
        await api.post("/knowledge-bases", { name, content });
        toast.success("Knowledge base created");
      }
      setEditor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete “${row.name}”?`)) return;
    try {
      await api.delete(`/knowledge-bases/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const previewText = (html) => {
    const plain = htmlToPlain(html || "");
    if (!plain) return "—";
    return plain.length > 120 ? `${plain.slice(0, 120)}…` : plain;
  };

  return (
    <div data-testid="knowledge-bases-page">
      <PageHeader
        title="Knowledge Bases"
        subtitle="Reference text the AI Agent node can use during a call. Updates apply immediately — no model training."
      >
        <Button
          className="rounded-lg"
          onClick={() => { setContentOverLimit(false); setEditor({ name: "", content: "" }); }}
          data-testid="kb-new-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" weight="bold" /> New knowledge base
        </Button>
      </PageHeader>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Books}
          title="No knowledge bases yet"
          description="Create a knowledge base with product facts, FAQs, or scripts, then select it on an AI Agent node."
          testid="kb-empty"
        />
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow overflow-x-auto" data-testid="kb-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Preview</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-28">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`kb-row-${r.id}`}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-md truncate">
                    {previewText(r.content)}
                  </TableCell>
                  <TableCell className="text-xs capitalize">{r.status || "active"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2"
                        onClick={() => {
                          setContentOverLimit(false);
                          setEditor({ id: r.id, name: r.name, content: r.content || "" });
                        }}
                        aria-label="Edit"
                      >
                        <PencilSimple className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive" onClick={() => remove(r)} aria-label="Delete">
                        <Trash className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
        <DialogContent className="max-w-2xl w-[min(94vw,42rem)] max-h-[90vh] flex flex-col" data-testid="kb-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editor?.id ? "Edit knowledge base" : "New knowledge base"}</DialogTitle>
            <DialogDescription>
              Paste facts the voice agent may use. Formatting (bold, headings, bullets) is kept for editing; the AI reads the text.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 overflow-y-auto flex-1 min-h-0 pr-0.5">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                data-testid="kb-name"
                value={editor?.name || ""}
                onChange={(e) => setEditor((s) => ({ ...s, name: e.target.value }))}
                placeholder="Product FAQ"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Content</Label>
              {editor && (
                <InlineRichHtmlEditor
                  key={editor.id || "new-kb"}
                  value={editor.content || ""}
                  maxLength={100000}
                  placeholder="Our premium package includes…"
                  testid="kb-content"
                  minHeightClass="min-h-[280px]"
                  maxHeightClass="max-h-[45vh]"
                  onChange={(html) => {
                    const plain = htmlToPlain(html);
                    setContentOverLimit(plain.length > 100000);
                    setEditor((s) => ({ ...s, content: html }));
                  }}
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setEditor(null)}>Cancel</Button>
            <Button className="rounded-lg" onClick={save} disabled={contentOverLimit} data-testid="kb-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
